import { format, formatDistanceToNowStrict } from "date-fns";
import type { LedgerReason, SubscriptionStatus, UsageSource } from "@/api/types";

export const MC_PER_CREDIT = 1000;

/** Credits with up to 2 decimals for small amounts, none for large ones. */
export function formatCredits(millicredits: number) {
	const value = millicredits / MC_PER_CREDIT;
	const abs = Math.abs(value);
	return value.toLocaleString(undefined, {
		maximumFractionDigits: abs >= 100 ? 0 : abs >= 1 ? 1 : 2,
	});
}

export const formatSignedCredits = (millicredits: number) =>
	`${millicredits > 0 ? "+" : millicredits < 0 ? "−" : ""}${formatCredits(Math.abs(millicredits))}`;

export const formatPrice = (cents: number, currency = "usd") =>
	(cents / 100).toLocaleString(undefined, {
		style: "currency",
		currency: currency.toUpperCase(),
		maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
	});

/** Provider cost: sub-cent precision so a $0.0021 caption doesn't read as $0.00. */
export function formatUsd(value: number) {
	if (value === 0) return "$0";
	if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
	return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export const formatCompact = (value: number) =>
	value.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });

export const formatNumber = (value: number) => value.toLocaleString();

export const formatLimit = (limit: number | null) =>
	limit == null ? "Unlimited" : limit.toLocaleString();

export function formatBytes(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB", "TB"];
	let value = bytes / 1024;
	let unit = 0;
	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024;
		unit++;
	}
	return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[unit]}`;
}

export const formatDate = (iso: string) => format(new Date(iso), "MMM d, yyyy");
export const formatDateTime = (iso: string) => format(new Date(iso), "MMM d, yyyy · HH:mm");
export const formatAgo = (iso: string | null) =>
	iso ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true }) : "Never";
/** `YYYY-MM-DD` buckets are UTC days; format them without shifting into the local zone. */
export const formatDay = (date: string, pattern = "MMM d") =>
	format(new Date(`${date}T12:00:00Z`), pattern);

/** Relative change vs. the previous period, or `null` when there's no baseline. */
export function delta(current: number, previous: number) {
	if (!previous) return null;
	return (current - previous) / previous;
}

export const formatPercent = (value: number) =>
	value.toLocaleString(undefined, { style: "percent", maximumFractionDigits: 1 });

export const SOURCE_LABELS: Record<UsageSource, string> = {
	openrouter: "AI generations",
	brightdata: "Competitor scrapes",
	zernio: "Publishing",
	storage: "Storage",
	other: "Other",
};

export const REASON_LABELS: Record<LedgerReason, string> = {
	plan_grant: "Plan credits",
	pack_purchase: "Credit pack",
	usage: "Usage",
	refund: "Refund",
	adjustment: "Adjustment",
	expiration: "Expired",
	promo: "Promo",
};

export const STATUS_LABELS: Record<SubscriptionStatus, string> = {
	trialing: "Trialing",
	active: "Active",
	past_due: "Past due",
	canceled: "Canceled",
	incomplete: "Incomplete",
	incomplete_expired: "Expired",
	unpaid: "Unpaid",
	paused: "Paused",
};

const FEATURE_LABELS: Record<string, string> = {
	"assistant.chat": "Assistant chat",
	"competitor.scrape": "Competitor scrape",
	"social.publish.x": "Publish to X",
	"storage.overage": "Storage above 5 GB",
	"insights.generate": "Insights",
};

export function featureLabel(feature: string) {
	if (FEATURE_LABELS[feature]) return FEATURE_LABELS[feature];
	const parts = feature.split(".").map((part) => part.replace(/[_-]/g, " "));
	return parts
		.map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
		.join(" · ");
}

export const sourceLabel = (key: string) => SOURCE_LABELS[key as UsageSource] ?? key;
